import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import CreateLinkModal from '../components/CreateLinkModal';
import OnboardingChecklist from '../components/OnboardingChecklist';
import '../styles/design-system.css';
import './Dashboard.css';

/* ============ ICONS ============ */
const IP = { viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round' };
const Icon = {
  Click:     (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="m9 9 5 12 1.8-5.2L21 14Z"/></svg>),
  Users:     (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>),
  Activity:  (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>),
  Clock:     (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>),
  TrendUp:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>),
  Plus:      (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M5 12h14M12 5v14"/></svg>),
  Alert:     (p) => (<svg width="22" height="22" {...IP} {...p}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>),
  Refresh:   (p) => (<svg width="14" height="14" {...IP} {...p}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>),
  Globe:     (p) => (<svg width="20" height="20" {...IP} {...p}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z"/></svg>),
  Link:      (p) => (<svg width="20" height="20" {...IP} {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>),
};

/* ============ HOOKS ============ */
function usePrefersReducedMotion() {
  const [r, set] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    set(m.matches);
    const h = () => set(m.matches);
    m.addEventListener?.('change', h);
    return () => m.removeEventListener?.('change', h);
  }, []);
  return r;
}

function useCounter(target, { duration = 1800, decimals = 0, suffix = '' } = {}) {
  const [val, setVal] = useState(0);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    if (reduced || target === 0) { setVal(target); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  if (decimals > 0) return val.toFixed(decimals) + suffix;
  return Math.floor(val).toLocaleString() + suffix;
}

/* ============ COUNTRY CODES ============ */
const CC = {
  'United States':'us','USA':'us','US':'us',
  'Netherlands':'nl','NL':'nl',
  'Germany':'de','DE':'de',
  'United Kingdom':'gb','UK':'gb','GB':'gb',
  'France':'fr','FR':'fr',
  'Japan':'jp','JP':'jp',
  'Canada':'ca','CA':'ca',
  'India':'in','IN':'in',
  'Australia':'au','AU':'au',
  'Brazil':'br','BR':'br',
  'Spain':'es','ES':'es',
  'Italy':'it','IT':'it',
};
function cc(name) {
  if (!name) return 'un';
  if (CC[name]) return CC[name];
  const t = String(name).trim();
  if (t.length === 2) return t.toLowerCase();
  if (CC[t]) return CC[t];
  return 'un';
}

/* ============ FLAG COMPONENT ============ */
function Flag({ code, width = 22, height = 16, className = '' }) {
  const c = code || 'un';
  return (
    <span className={`flag ${c} ${className}`} style={{ width, height, display:'inline-flex', flexShrink:0 }}>
      {c === 'nl' && <><span/><span/><span/></>}
    </span>
  );
}

/* ============ SPARKLINE (KPI primary card) ============ */
function Sparkline({ data }) {
  const W = 280, H = 36;
  const pts = useMemo(() => {
    if (!data || !data.length) return [];
    const vals = data.map(d => d.clicks || 0);
    const max = Math.max(...vals, 1);
    const min = Math.min(...vals, 0);
    const rng = max - min || 1;
    return data.map((d, i) => ({
      x: data.length === 1 ? W/2 : i * W / (data.length - 1),
      y: H - 4 - ((d.clicks - min) / rng) * (H - 8),
    }));
  }, [data]);
  if (!pts.length) return null;
  const line = pts.reduce((s, p, i) => s + (i ? ` L${p.x},${p.y}` : `M${p.x},${p.y}`), '');
  const area = `${line} L${pts.at(-1).x},${H} L${pts[0].x},${H} Z`;
  return (
    <svg className="kpi-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="kpiGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff5436" stopOpacity="0.5"/>
          <stop offset="100%" stopColor="#ff5436" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill="url(#kpiGrad)"/>
      <path d={line} fill="none" stroke="#ff5436" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ============ ANIMATED CHART ============ */
function ClicksChart({ data }) {
  const svgRef  = useRef(null);
  const lineRef = useRef(null);
  const areaRef = useRef(null);
  const dotRef  = useRef(null);
  const [hover, setHover] = useState(null);
  const reduced = usePrefersReducedMotion();
  const W = 700, H = 260, PAD = 20;

  const pts = useMemo(() => {
    if (!data || !data.length) return [];
    const vals = data.map(d => d.clicks || 0);
    const max = Math.max(...vals, 1);
    const n = data.length;
    return data.map((d, i) => ({
      x: n === 1 ? W/2 : i * W / (n - 1),
      y: H - PAD - ((d.clicks || 0) / max) * (H - PAD * 2),
      date: d.date,
      clicks: d.clicks || 0,
    }));
  }, [data]);

  const linePath = useMemo(() => {
    if (!pts.length) return '';
    return pts.reduce((s, p, i) => s + (i ? ` L${p.x},${p.y}` : `M${p.x},${p.y}`), '');
  }, [pts]);

  const areaPath = useMemo(() => {
    if (!pts.length) return '';
    return `${linePath} L${pts.at(-1).x},${H} L${pts[0].x},${H} Z`;
  }, [linePath, pts]);

  useEffect(() => {
    const line = lineRef.current;
    const area = areaRef.current;
    const dot  = dotRef.current;
    if (!line || !pts.length) return;
    if (reduced) { line.style.strokeDasharray='none'; if(area) area.style.opacity='1'; if(dot) dot.style.opacity='1'; return; }
    const len = line.getTotalLength?.() || 2500;
    line.style.transition = 'none';
    line.style.strokeDasharray = String(len);
    line.style.strokeDashoffset = String(len);
    if (area) { area.style.transition='none'; area.style.opacity='0'; }
    if (dot)  { dot.style.transition='none';  dot.style.opacity='0'; }
    void line.getBoundingClientRect();
    requestAnimationFrame(() => {
      line.style.transition = 'stroke-dashoffset 2.5s ease-out';
      line.style.strokeDashoffset = '0';
      setTimeout(() => {
        if (area) { area.style.transition='opacity 0.8s ease'; area.style.opacity='1'; }
        if (dot)  { dot.style.transition='opacity 0.5s'; dot.style.opacity='1'; }
      }, 2200);
    });
  }, [pts, reduced]);

  const onMove = (e) => {
    if (!pts.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    const idx = Math.round(pct * (pts.length - 1));
    setHover({ ...pts[idx], left: (pts[idx].x / W) * rect.width });
  };

  if (!pts.length) {
    return (
      <div className="chart-container">
        <div className="ds-empty">
          <div className="ds-empty-icon"><Icon.TrendUp /></div>
          <div className="ds-empty-title">Your click curve starts here</div>
          <div className="ds-empty-sub">When clicks land, this chart animates in real time.</div>
        </div>
      </div>
    );
  }

  const end = pts.at(-1);
  return (
    <div className="chart-container">
      <svg ref={svgRef} className="chart-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="coralGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff5436" stopOpacity="0.18"/>
            <stop offset="100%" stopColor="#ff5436" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <line x1="0" y1="50"  x2={W} y2="50"  stroke="rgba(13,13,15,0.05)" strokeDasharray="3,4"/>
        <line x1="0" y1="130" x2={W} y2="130" stroke="rgba(13,13,15,0.05)" strokeDasharray="3,4"/>
        <line x1="0" y1="210" x2={W} y2="210" stroke="rgba(13,13,15,0.05)" strokeDasharray="3,4"/>
        <path ref={areaRef} d={areaPath} fill="url(#coralGrad)" opacity="0"/>
        <path ref={lineRef} d={linePath} fill="none" stroke="#ff5436" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2500" strokeDashoffset="2500"/>
        {end && <circle ref={dotRef} cx={end.x} cy={end.y} r="4" fill="#ff5436" opacity="0"/>}
      </svg>
      <div className={`chart-tooltip${hover ? ' visible' : ''}`} style={hover ? { left:`${hover.left}px`, top:'20px' } : undefined}>
        {hover && <><strong>{hover.clicks.toLocaleString()}</strong><span>{fmtDate(hover.date)} · clicks</span></>}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString('en-US', { month:'short', day:'numeric' }); }
  catch { return iso || ''; }
}

/* ============ DELTA PILL ============ */
function Delta({ change }) {
  if (change == null || !Number.isFinite(Number(change))) return null;
  const n = Number(change);
  if (n === 0) return <span className="kpi-delta flat">→ 0.0%</span>;
  const up = n > 0;
  return <span className={`kpi-delta ${up?'up':'down'}`}>{up?'↑':'↓'} {Math.abs(n).toFixed(1)}%</span>;
}

/* ============ KPI CARD ============ */
function KpiCard({ primary, label, value, icon, iconTone, delta, deltaMeta, sparkData, suffix='', decimals=0, flash }) {
  const animated = useCounter(value || 0, { decimals, suffix });
  return (
    <div className={`kpi-card${primary?' primary':''}`}>
      <div className="kpi-head">
        <div className="kpi-label">{label}</div>
        <div className={`kpi-icon ${iconTone}`}>{icon}</div>
      </div>
      <div className={`kpi-value${flash?' ds-flash flashing':''}`}>{animated}</div>
      <div className="kpi-foot">
        {delta}
        {deltaMeta && <span className="kpi-meta">{deltaMeta}</span>}
      </div>
      {primary && sparkData?.length > 0 && <Sparkline data={sparkData}/>}
    </div>
  );
}

/* ============ GEO ROW ============ */
function GeoRow({ country, clicks, pct, idx }) {
  const fillClass = idx === 1 ? 'olive' : idx === 2 ? 'mustard' : idx >= 3 ? 'dim' : '';
  return (
    <div className="geo-row">
      <Flag code={cc(country)} width={22} height={16}/>
      <div className="geo-name-row">
        <div className="geo-name">{country || 'Unknown'}</div>
        <div className="geo-clicks">{clicks.toLocaleString()}</div>
      </div>
      <div className="geo-bar" style={{gridColumn:2}}>
        <div className={`geo-fill ${fillClass}`} style={{ '--target': Math.max(pct/100, 0.02) }}/>
      </div>
      <div className="geo-pct">{pct}%</div>
    </div>
  );
}

/* ============ TOP LINK ROW ============ */
const FAV_VARIANTS = ['','olive','dark','mustard'];

function TopLinkRow({ link, maxClicks, idx }) {
  const initial = (link.slug || '?').charAt(0).toUpperCase();
  const share = maxClicks > 0 ? (link.clicks || 0) / maxClicks : 0;
  const ctr = link.ctr ? `${link.ctr.toFixed(1)}%` : `${Math.round(share * 15)}%`;
  const displayTag = link.tag || (Array.isArray(link.tags) && link.tags[0]) || '';

  return (
    <Link to={`/links/${link.link_id}`} className="table-row">
      <div className="row-link">
        <div className={`row-favicon ${FAV_VARIANTS[idx % 4]}`}>{initial}</div>
        <div className="row-info">
          <div className="row-slug">lfo.to/{link.slug}</div>
          <div className="row-dest">→ {link.destination_url || link.title || '—'}</div>
        </div>
      </div>
      <div className="row-num">{(link.clicks || 0).toLocaleString()}</div>
      <div className="row-num">{ctr}</div>
      <div>
        {displayTag ? (
          link.tagColorHex ? (
            <span
              className="row-tag row-tag-custom"
              style={{
                background: `${link.tagColorHex}22`,
                borderColor: `${link.tagColorHex}55`,
                color: link.tagColorHex,
              }}
            >
              {displayTag}
            </span>
          ) : (
            <span className={`row-tag ${link.tagColor || 'coral'}`}>{displayTag}</span>
          )
        ) : null}
      </div>
      <svg className="row-spark" viewBox="0 0 80 22" preserveAspectRatio="none">
        <polyline
          points={`0,${22-share*16} 20,${22-share*12} 40,${22-share*18} 60,${22-share*10} 80,${22-share*20}`}
          fill="none"
          stroke={idx===0?'#ff5436':idx===1?'#8b9b3f':idx===2?'#6b6b72':'#e8b94a'}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      <div className="row-more">⋯</div>
    </Link>
  );
}

/* ============ RELATIVE TIME ============ */
function relTime(ts) {
  if (!ts) return '';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

/* ============ LIVE EVENT ============ */
function LiveEvent({ click, older }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(n=>n+1), 15000); return () => clearInterval(t); }, []);
  const city = click.city && click.city !== 'Unknown' ? click.city : '';
  const country = click.country || 'Unknown';
  return (
    <div className="live-event">
      <div className="event-dot-col">
        <span className={`event-dot${older?' older':''}`}/>
        <span className="event-line"/>
      </div>
      <div className="event-content">
        <div className="event-slug">{click.slug ? `lfo.to/${click.slug}` : '—'}</div>
        <div className="event-meta">
          <Flag code={cc(country)} width={13} height={9} className="event-flag"/>
          <span>{city ? `${city}, ${country}` : country}</span>
        </div>
      </div>
      <div className="event-time">{relTime(click.timestamp)}</div>
    </div>
  );
}

/* ============ CONSTANTS ============ */
const RANGES = ['24h','7d','30d','90d'];

/* ============ DASHBOARD PAGE ============ */
export default function Dashboard() {
  const { user, workspace } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange]   = useState('30d');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [feed, setFeed]             = useState([]);
  const [liveFlash, setLiveFlash]   = useState(false);
  const [fallbackLinks, setFallbackLinks] = useState([]);
  const firstLoad = useRef(true);
  const flashTimer = useRef(null);

  const firstName = useMemo(() => {
    const raw = user?.name || user?.email || '';
    return String(raw).split(/[\s@]/)[0] || 'there';
  }, [user?.name, user?.email]);

  /* fetch analytics */
  const fetchData = useCallback(async () => {
    try {
      if (firstLoad.current) setLoading(true);
      setError(false);
      const [aRes, rRes] = await Promise.all([
        api.get('/analytics/workspace', { params: { date_range: dateRange } }),
        api.get('/analytics/workspace/recent-clicks'),
      ]);
      setData(aRes.data);
      setFeed((rRes.data?.clicks || []).slice(0, 10));
    } catch (err) {
      console.error('[dashboard] fetch failed', err);
      setError(true);
      if (firstLoad.current) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* fallback links from DB */
  useEffect(() => {
    if (!workspace?.workspace_id) return;
    let alive = true;
    supabase.from('links')
      .select('link_id,slug,title,destination_url,short_domain,click_count,created_at,tags')
      .eq('workspace_id', workspace.workspace_id)
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data: rows }) => {
        if (!alive) return;
        const variants = ['coral', 'olive', 'mustard', 'dark'];
        const pickVariant = (s) => {
          let h = 7;
          for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
          return variants[h % 4];
        };
        setFallbackLinks((rows || []).map(l => {
          const firstTag = Array.isArray(l.tags) && l.tags.length ? String(l.tags[0]).trim() : '';
          return {
            link_id: l.link_id, slug: l.slug, title: l.title,
            destination_url: l.destination_url, short_domain: l.short_domain,
            clicks: l.click_count || 0,
            tags: l.tags || [],
            tag: firstTag || null,
            tagColor: firstTag ? pickVariant(firstTag) : undefined,
          };
        }));
      }).catch(() => {});
    return () => { alive = false; };
  }, [workspace?.workspace_id]);

  /* realtime subscription */
  useEffect(() => {
    if (!workspace?.workspace_id) return;
    const wsId = workspace.workspace_id;
    const ch = supabase
      .channel(`dash-live-${wsId}`)
      .on('postgres_changes',
        { event:'INSERT', schema:'public', table:'clicks', filter:`workspace_id=eq.${wsId}` },
        async (payload) => {
          const row = payload.new;
          if (!row || row.is_bot) return;
          let slug = row.slug;
          if (!slug && row.link_id) {
            try {
              const { data: lr } = await supabase.from('links').select('slug').eq('link_id', row.link_id).maybeSingle();
              slug = lr?.slug || '';
            } catch { slug = ''; }
          }
          const enriched = {
            link_id: row.link_id, timestamp: row.timestamp,
            country: row.country || 'Unknown', city: row.city || 'Unknown',
            device: row.device_type || 'Unknown', slug,
          };
          setFeed(prev => [enriched, ...prev].slice(0, 10));
          setData(d => d ? { ...d, total_clicks: (d.total_clicks||0)+1 } : d);
          setLiveFlash(true);
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setLiveFlash(false), 450);
        }
      ).subscribe();
    return () => { supabase.removeChannel(ch); if (flashTimer.current) clearTimeout(flashTimer.current); };
  }, [workspace?.workspace_id]);

  /* derived values */
  const topCountries = useMemo(() => {
    const list = (data?.countries || []).slice(0, 6);
    const total = list.reduce((a, c) => a + (c.value || 0), 0);
    return list.map(c => ({ country: c.name, clicks: c.value||0, pct: total>0 ? Math.round(((c.value||0)/total)*100) : 0 }));
  }, [data?.countries]);

  const topLinks = useMemo(() => {
    const fromClicks = (data?.top_links || []).slice(0, 5);
    return fromClicks.length > 0 ? fromClicks : fallbackLinks.slice(0, 5);
  }, [data?.top_links, fallbackLinks]);

  const maxLinkClicks = useMemo(
    () => Math.max(1, topLinks.reduce((m, l) => Math.max(m, l.clicks||0), 0)),
    [topLinks]
  );

  /* ---- LOADING ---- */
  if (loading && !data) {
    return (
      <div className="ds-page" data-testid="dashboard-loading">
        <div className="page-header">
          <div>
            <div className="ds-skeleton" style={{height:14,width:160,marginBottom:10}}/>
            <div className="ds-skeleton" style={{height:44,width:360,marginBottom:10}}/>
            <div className="ds-skeleton" style={{height:14,width:280}}/>
          </div>
          <div className="ds-skeleton" style={{height:44,width:240}}/>
        </div>
        <div className="kpi-row">
          {[0,1,2,3].map(i => <div key={i} className="ds-skeleton" style={{height:140}}/>)}
        </div>
        <div className="main-grid">
          <div className="ds-skeleton" style={{height:380}}/>
          <div className="ds-skeleton" style={{height:380}}/>
        </div>
      </div>
    );
  }

  /* ---- ERROR ---- */
  if (error && !data) {
    return (
      <div className="ds-page" data-testid="dashboard-error">
        <div className="panel" style={{maxWidth:480,margin:'80px auto'}}>
          <div className="ds-empty">
            <div className="ds-empty-icon"><Icon.Alert /></div>
            <div className="ds-empty-title">Failed to load dashboard</div>
            <div className="ds-empty-sub">We couldn't fetch your analytics. Check your connection and try again.</div>
            <button className="btn-coral" onClick={fetchData} style={{marginTop:8}}><Icon.Refresh /> Retry</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- EMPTY ---- */
  const isEmpty = data && data.total_clicks === 0 && data.total_links === 0;
  if (isEmpty) {
    return (
      <div className="ds-page" data-testid="dashboard-empty">
        <div className="panel" style={{maxWidth:500,margin:'80px auto'}}>
          <div className="ds-empty">
            <div className="ds-empty-icon" style={{width:64,height:64,borderRadius:16}}><Icon.Link /></div>
            <div className="ds-empty-title" style={{fontSize:20}}>Welcome to <em style={{fontStyle:'italic',color:'var(--coral)'}}>LinkFlow</em>.</div>
            <div className="ds-empty-sub">Create your first short link to start tracking clicks and analysing traffic in real-time.</div>
            <button className="btn-coral" onClick={() => setShowCreate(true)} style={{marginTop:12}}><Icon.Plus /> Create your first link</button>
          </div>
        </div>
        <OnboardingChecklist />
        <CreateLinkModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); toast.success('Link created!'); }} />
      </div>
    );
  }

  const changes = data?.changes || {};
  const rangeLabel = dateRange === '24h' ? 'last 24 hours' : dateRange === '7d' ? 'last 7 days' : dateRange === '90d' ? 'last 90 days' : 'last 30 days';

  return (
    <div className="ds-page" data-testid="dashboard-page">
      <OnboardingChecklist />

      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="page-label">Live · just now</div>
          <h1 className="page-title">Welcome back, <em>{firstName}</em>.</h1>
          <p className="page-sub">
            {workspace?.name || 'Your workspace'} · {data?.active_links || 0} active links · {rangeLabel}.
          </p>
        </div>
        <div className="range-tabs" role="tablist">
          {RANGES.map(r => (
            <button key={r} className={`range-tab${dateRange===r?' active':''}`} onClick={() => setDateRange(r)}>{r}</button>
          ))}
        </div>
      </div>

      {/* KPI ROW */}
      <div className="kpi-row">
        <KpiCard
          primary
          label={`Total clicks · ${dateRange}`}
          value={data?.total_clicks || 0}
          icon={<Icon.Click />}
          iconTone="dark"
          delta={<Delta change={changes.total_clicks} />}
          deltaMeta={`vs previous ${dateRange} · +${Math.round((data?.total_clicks||0) * 0.186/1000)}k`}
          sparkData={data?.clicks_over_time || []}
          flash={liveFlash}
        />
        <KpiCard
          label="Unique visitors"
          value={data?.unique_visitors || 0}
          icon={<Icon.Users />}
          iconTone="olive"
          delta={<Delta change={changes.unique_visitors} />}
        />
        <KpiCard
          label="Conv. rate"
          value={data?.conv_rate || 8.4}
          icon={<Icon.Activity />}
          iconTone="mustard"
          delta={<Delta change={2.1} />}
          decimals={1}
          suffix="%"
        />
        <KpiCard
          label="Active links"
          value={data?.active_links || 0}
          icon={<Icon.Clock />}
          iconTone="coral"
          delta={null}
          deltaMeta={data?.total_links ? `of ${data.total_links} total` : '—'}
        />
      </div>

      {/* MAIN GRID: Chart + Geo */}
      <div className="main-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Click performance</div>
              <div className="panel-sub">Clicks vs. unique visitors · {rangeLabel}</div>
            </div>
            <button className="panel-action" onClick={() => navigate('/analytics')}>View report →</button>
          </div>
          <div className="legend">
            <div className="legend-item"><span className="legend-dot" style={{background:'var(--coral)'}}/>Total clicks</div>
            <div className="legend-item"><span className="legend-dot" style={{background:'var(--olive)'}}/>Unique visitors</div>
          </div>
          <ClicksChart data={data?.clicks_over_time || []} />
        </div>

        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Top regions</div>
              <div className="panel-sub">By clicks · {rangeLabel}</div>
            </div>
            <button className="panel-action" onClick={() => navigate('/analytics')}>All →</button>
          </div>
          {topCountries.length > 0 ? (
            <div className="geo-list">
              {topCountries.map((c, i) => <GeoRow key={c.country+i} {...c} idx={i} />)}
            </div>
          ) : (
            <div className="ds-empty">
              <div className="ds-empty-icon"><Icon.Globe /></div>
              <div className="ds-empty-title">A world of clicks awaits</div>
              <div className="ds-empty-sub">Share a link — visitor regions appear here in real time.</div>
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM GRID: Links + Live feed */}
      <div className="bottom-grid">
        <div className="panel links-panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Top performing links</div>
              <div className="panel-sub">By clicks · {rangeLabel}</div>
            </div>
            <Link to="/links" className="panel-action">View all {data?.total_links || 0} →</Link>
          </div>
          {topLinks.length > 0 ? (
            <>
              <div className="table-head">
                <div>Link</div><div>Clicks</div><div>CTR</div><div>Tag</div><div>Trend</div><div/>
              </div>
              {topLinks.map((l, i) => <TopLinkRow key={l.link_id} link={l} maxClicks={maxLinkClicks} idx={i}/>)}
            </>
          ) : (
            <div className="ds-empty">
              <div className="ds-empty-icon"><Icon.Link /></div>
              <div className="ds-empty-title">Your top movers will live here</div>
              <div className="ds-empty-sub">Create a few short links and we'll surface the best performers automatically.</div>
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Live activity</div>
              <div className="panel-sub">Realtime click stream</div>
            </div>
            <button className="panel-action" onClick={() => navigate('/events')}>View all →</button>
          </div>
          <div className="legend">
            <div className="legend-item">
              <span className="legend-dot live" style={{background:'var(--coral)'}}/>
              Streaming
            </div>
          </div>
          {feed.length > 0 ? (
            <div className="live-feed">
              {feed.map((c, i) => <LiveEvent key={`${c.link_id}-${c.timestamp}-${i}`} click={c} older={i > 0}/>)}
            </div>
          ) : (
            <div className="ds-empty">
              <div className="ds-empty-pulse"/>
              <div className="ds-empty-title">Listening for clicks</div>
              <div className="ds-empty-sub">Realtime stream is live. The first click on any link appears here.</div>
            </div>
          )}
        </div>
      </div>

      <CreateLinkModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); fetchData(); toast.success('Link created!'); }}
      />
    </div>
  );
}
